import React from 'react';
import { Box, Button, LinearProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { SxProps, Theme } from '@mui/material/styles';
import { useAudioRecognition } from '../AudioRecognitionContext';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import CalibrationDiagnosticsPanel from './CalibrationDiagnosticsPanel';

type CalibrationPanelProps = {
  title?: string;
  description?: string;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
};

const calibrationPrimaryButtonSx = {
  minHeight: '42px',
  minWidth: '180px',
  paddingX: '18px',
  borderRadius: '12px',
  backgroundColor: '#469F6A',
  color: '#FFFFFF',
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'none',
  boxShadow: '0 10px 22px rgba(22, 96, 58, 0.24)',
  transition: 'background-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
  '&:hover': {
    backgroundColor: '#3a8556',
    boxShadow: '0 12px 26px rgba(22, 96, 58, 0.32)',
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': {
    backgroundColor: '#767C8C',
    color: '#ADB1B7',
  },
};

const calibrationSecondaryButtonSx = {
  minHeight: '42px',
  paddingX: '18px',
  borderRadius: '12px',
  borderColor: '#767C8C',
  color: '#ECECED',
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'none',
  '&:hover': {
    borderColor: '#6F87F1',
    backgroundColor: 'rgba(111, 135, 241, 0.08)',
  },
  '&.Mui-disabled': {
    borderColor: 'rgba(118, 124, 140, 0.45)',
    color: 'rgba(173, 177, 183, 0.6)',
  },
};

const calibrationDangerButtonSx = {
  ...calibrationSecondaryButtonSx,
  '&:hover': {
    borderColor: '#C24444',
    backgroundColor: 'rgba(194, 68, 68, 0.08)',
  },
};

const formatCalibrationWarning = (warning: string) => {
  switch (warning) {
    case 'low-signal-to-noise':
      return 'Room noise is high compared with the piano.';
    case 'limited-single-note-anchors':
      return 'Too few single-note samples were captured.';
    case 'limited-chord-evidence':
      return 'Too few clear chord frames were captured.';
    default:
      return warning.replace(/-/g, ' ');
  }
};

const CalibrationPanel: React.FC<CalibrationPanelProps> = ({
  title = 'Calibrate your piano',
  description = 'When your room is quiet, play a few guided notes so MuseFlow can adapt to your piano.',
  sx,
  children,
}) => {
  const audioRecognitionEnabled = useFeatureFlag(FeatureFlag.USE_AUDIO_RECOGNITION);
  const {
    calibration,
    calibrationProfile,
    startCalibration,
    cancelCalibration,
    clearCalibrationProfile,
  } = useAudioRecognition();
  const calibrationComplete = calibration.phase === 'complete';

  return (
    <Box
      data-calibration-panel
      sx={{
        marginTop: '22px',
        marginBottom: '4px',
        marginLeft: '28px',
        marginRight: '28px',
        padding: { xs: '18px 16px 16px', md: '22px 22px 18px' },
        borderRadius: '18px',
        border: '1px solid rgba(111, 135, 241, 0.24)',
        background:
          'linear-gradient(180deg, rgba(15, 27, 50, 0.96) 0%, rgba(7, 13, 24, 0.99) 100%)',
        boxShadow: '0 18px 34px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255,255,255,0.025)',
        ...sx,
      }}
    >
      <Box
        data-calibration-title
        sx={{
          color: '#F2F6FF',
          fontSize: { xs: '18px', md: '20px' },
          fontWeight: 750,
          letterSpacing: 0,
          lineHeight: 1.25,
          marginBottom: '8px',
          textAlign: 'center',
        }}
      >
        {title}
      </Box>
      <Box
        sx={{
          color: 'rgba(226, 234, 255, 0.78)',
          fontSize: '13px',
          lineHeight: 1.55,
          maxWidth: '620px',
          marginX: 'auto',
          marginBottom: children ? '12px' : '20px',
          textAlign: 'center',
        }}
      >
        {description}
      </Box>

      {children}

      {calibrationProfile && !calibration.running && !calibrationComplete && (
        <Box
          data-calibration-profile-summary
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '14px',
            padding: '10px 12px',
            borderRadius: '12px',
            backgroundColor: 'rgba(111, 135, 241, 0.1)',
            border: '1px solid rgba(111, 135, 241, 0.16)',
          }}
        >
          <Box sx={{ color: '#C4D4FF', fontSize: '12px', fontWeight: 700 }}>
            {calibrationProfile.label}
          </Box>
          <Box sx={{ color: 'rgba(226, 234, 255, 0.68)', fontSize: '12px' }}>
            {calibrationProfile.noteTemplates.length} piano sample
            {calibrationProfile.noteTemplates.length === 1 ? '' : 's'} saved
          </Box>
        </Box>
      )}

      {calibrationProfile?.quality && (
        <Box
          sx={{
            marginTop: '-4px',
            marginBottom: '12px',
            padding: '9px 11px',
            borderRadius: '10px',
            border:
              calibrationProfile.quality.warnings.length > 0
                ? '1px solid rgba(255, 210, 125, 0.24)'
                : '1px solid rgba(125, 255, 165, 0.18)',
            backgroundColor:
              calibrationProfile.quality.warnings.length > 0
                ? 'rgba(255, 210, 125, 0.07)'
                : 'rgba(125, 255, 165, 0.06)',
            color: 'rgba(226, 234, 255, 0.78)',
            fontSize: '11px',
            lineHeight: 1.45,
          }}
        >
          Calibration sample quality {Math.round(calibrationProfile.quality.score * 100)}%. Signal
          to noise {calibrationProfile.quality.signalToNoiseRatio.toFixed(1)}x.
          {calibrationProfile.quality.warnings.map((warning) => (
            <Box key={warning} sx={{ color: '#FFD27D', marginTop: '3px' }}>
              {formatCalibrationWarning(warning)} Recalibrate before a benchmark run.
            </Box>
          ))}
        </Box>
      )}

      {audioRecognitionEnabled ? (
        calibrationComplete ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '138px',
              marginBottom: '14px',
              borderRadius: '14px',
              background:
                'linear-gradient(180deg, rgba(25, 74, 51, 0.35) 0%, rgba(8, 27, 29, 0.46) 100%)',
              border: '1px solid rgba(125, 255, 165, 0.26)',
              padding: '18px 14px 16px',
              textAlign: 'center',
              '@keyframes calibrationCompletePop': {
                '0%': { transform: 'scale(0.76)', opacity: 0 },
                '65%': { transform: 'scale(1.08)', opacity: 1 },
                '100%': { transform: 'scale(1)', opacity: 1 },
              },
              '@keyframes calibrationCompleteGlow': {
                '0%': { boxShadow: '0 0 0 0 rgba(125, 255, 165, 0.34)' },
                '100%': { boxShadow: '0 0 0 18px rgba(125, 255, 165, 0)' },
              },
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                marginBottom: '10px',
                backgroundColor: 'rgba(125, 255, 165, 0.16)',
                color: '#7DFFA5',
                border: '1px solid rgba(125, 255, 165, 0.42)',
                animation:
                  'calibrationCompletePop 420ms ease-out both, calibrationCompleteGlow 900ms ease-out 120ms both',
              }}
            >
              <CheckIcon sx={{ fontSize: '26px' }} />
            </Box>
            <Box
              sx={{
                color: '#F2F6FF',
                fontSize: '17px',
                fontWeight: 800,
                lineHeight: 1.25,
                marginBottom: '5px',
              }}
            >
              Complete
            </Box>
            <Box sx={{ color: 'rgba(226, 234, 255, 0.78)', fontSize: '12px', lineHeight: 1.5 }}>
              Audio recognition profile created.
            </Box>
          </Box>
        ) : calibration.phase !== 'idle' ? (
          <Box
            data-calibration-active-prompt
            sx={{
              marginBottom: '14px',
              borderRadius: '14px',
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.06)',
              padding: '16px 16px 14px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '10px',
                flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '7px 11px',
                  borderRadius: '999px',
                  backgroundColor: calibration.running
                    ? calibration.stageLabel === 'Capture'
                      ? 'rgba(125, 255, 165, 0.14)'
                      : 'rgba(121, 216, 255, 0.14)'
                    : 'rgba(255,255,255,0.07)',
                  color: calibration.running
                    ? calibration.stageLabel === 'Capture'
                      ? '#7DFFA5'
                      : '#79D8FF'
                    : '#D9E5FF',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                {calibration.stageLabel}
              </Box>

              {calibration.timerLabel && (
                <Box
                  sx={{
                    color: '#DDE7FF',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {calibration.timerLabel}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                color: '#F2F6FF',
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: 1.45,
                marginBottom: '6px',
              }}
            >
              {calibration.instruction}
            </Box>

            <Box
              sx={{
                color: 'rgba(226, 234, 255, 0.78)',
                fontSize: '12px',
                lineHeight: 1.6,
                minHeight: '38px',
              }}
            >
              {calibration.detail}
            </Box>

            {calibration.heardNotes.length > 0 && (
              <Box
                sx={{
                  marginTop: '10px',
                  color: '#B9D4FF',
                  fontSize: '11px',
                  letterSpacing: '0.02em',
                }}
              >
                Hearing: {calibration.heardNotes.join(', ')}
              </Box>
            )}
          </Box>
        ) : null
      ) : (
        <Box
          sx={{
            color: '#FFB4B4',
            fontSize: '12px',
            lineHeight: 1.5,
            minHeight: '36px',
            marginBottom: calibration.running ? '10px' : '14px',
          }}
        >
          Audio recognition input is turned off in Experimental Features, so calibration is
          unavailable.
        </Box>
      )}

      {calibration.running && (
        <Box sx={{ marginBottom: '14px' }}>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, calibration.progress * 100))}
            sx={{
              height: '8px',
              borderRadius: '999px',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: '999px',
                background: 'linear-gradient(90deg, #79D8FF 0%, #7DFFA5 100%)',
              },
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        {!calibration.running && (
          <Button
            data-calibration-start
            variant="contained"
            disabled={!audioRecognitionEnabled}
            onClick={() => {
              void startCalibration().catch(() => {
                // Calibration state already reports failures.
              });
            }}
            sx={calibrationPrimaryButtonSx}
          >
            {calibrationProfile ? 'Recalibrate' : 'Start calibration'}
          </Button>
        )}

        {calibration.running && (
          <Button
            variant="outlined"
            onClick={() => {
              cancelCalibration();
            }}
            sx={calibrationSecondaryButtonSx}
          >
            Cancel
          </Button>
        )}

        {calibrationProfile && !calibration.running && (
          <Button
            variant="outlined"
            onClick={() => {
              clearCalibrationProfile();
            }}
            sx={calibrationDangerButtonSx}
          >
            Reset profile
          </Button>
        )}
      </Box>

      {audioRecognitionEnabled && (
        <Box
          component="details"
          data-calibration-details
          sx={{
            marginTop: '14px',
            borderRadius: '12px',
            border: '1px solid rgba(121, 216, 255, 0.18)',
            backgroundColor: 'rgba(3, 10, 19, 0.44)',
            overflow: 'hidden',
            '&[open] .calibration-details-chevron': {
              transform: 'rotate(180deg)',
            },
          }}
        >
          <Box
            component="summary"
            sx={{
              minHeight: '44px',
              padding: '0 14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              color: '#DCE7FF',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              listStyle: 'none',
              '&::-webkit-details-marker': { display: 'none' },
            }}
          >
            Calibration details
            <ExpandMoreIcon
              className="calibration-details-chevron"
              sx={{ fontSize: '20px', transition: 'transform 160ms ease' }}
            />
          </Box>
          <Box sx={{ padding: '0 12px 12px' }}>
            <CalibrationDiagnosticsPanel />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CalibrationPanel;
